import React, { useRef } from "react";
import type { Order } from "~/features/orders/types-order";
import type { EditingState } from "~/features/orders/types";
import { getOrderCity } from "~/shared/utils/order-helpers";
import { formatDateWithYear, formatTimeRequired, formatWindow } from "~/shared/utils/formatting";
import { liveTransferOutcomeOptions } from "~/features/orders/constants";
import { OutcomeBadge } from "./OutcomeBadge";
import { WindowSelect } from "~/shared/components/WindowSelect";

type TransferOrder = Readonly<{
    order: Order;
    transferReason: string;
}>;

type Step3ReviewProps = Readonly<{
    transferOrders: TransferOrder[];
    processedOrders: Order[];
    handledTransfers: Set<string>;
    editingInline: string | null;
    editing: EditingState | null;
    onEdit: (order: Order, transferReason: string) => void;
    onCancelEdit: () => void;
    onSaveEdit: () => void;
    onUpdateEditing: (updates: Partial<EditingState>) => void;
    onBackToSelection: () => void;
}>;

const whiteGloveLabel = (flag?: boolean) => flag ? 'Yes' : 'No';
const rowClass = (flag?: boolean) => flag ? 'whiteglove-yes' : 'whiteglove-no';

export function Step3Review({
    transferOrders,
    processedOrders,
    handledTransfers,
    editingInline,
    editing,
    onEdit,
    onCancelEdit,
    onSaveEdit,
    onUpdateEditing,
    onBackToSelection,
}: Step3ReviewProps) {
    const dateTypingRef = useRef(false);
    return (
        <div>
            {/* Header */}
            <div className="table-container" style={{ marginBottom: 16 }}>
                <div style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ fontWeight: 600 }}>Review Outcomes</div>
                    <div className="spacer" />
                    {transferOrders.length > 0 && (
                        <span className="badge outcome-transfer">
                            📞 {transferOrders.filter(t => handledTransfers.has(t.order.id)).length} / {transferOrders.length} Transfers Handled
                        </span>
                    )}
                    <a className="btn btn-secondary" href="/scheduled">Open Scheduled</a>
                    <button className="secondary" onClick={onBackToSelection}>Start Over</button>
                </div>
            </div>

            {/* Transfers Section */}
            {transferOrders.length > 0 && (
                <div className="table-container table-container--scroll-inner" style={{ marginBottom: 16, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ padding: '16px', borderBottom: '1px solid var(--border)', background: '#FFF3CD', flexShrink: 0 }}>
                        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#856404' }}>
                            ⚠️ NEEDS ATTENTION: {transferOrders.length} Transfer{transferOrders.length !== 1 ? 's' : ''}
                        </h3>
                    </div>
                    <div className="table-scroll-wrapper">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>SM</th>
                                    <th>Patient</th>
                                    <th>White Glove</th>
                                    <th>City</th>
                                    <th>Phone</th>
                                    <th>Transfer Reason</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transferOrders.map(({ order, transferReason }) => {
                                    const isEditing = editingInline === order.id;
                                    const isHandled = handledTransfers.has(order.id);
                                    const rowStyle = {
                                        borderLeft: isEditing ? '4px solid var(--warning-amber)' : isHandled ? '4px solid var(--success-green)' : undefined,
                                    } as React.CSSProperties;

                                    return (
                                        <React.Fragment key={order.id}>
                                            <tr className={rowClass(order.white_glove_service)} style={rowStyle}>
                                                <td><strong>{order.sm_number}</strong></td>
                                                <td>{order.patient_name}</td>
                                                <td>{whiteGloveLabel(order.white_glove_service)}</td>
                                                <td>{getOrderCity(order)}</td>
                                                <td>
                                                    <a href={`tel:${order.phone}`} style={{ textDecoration: 'none', color: 'var(--primary-blue)' }}>
                                                        {order.phone}
                                                    </a>
                                                </td>
                                                <td>
                                                    <span style={{ fontSize: 13, fontStyle: 'italic', color: 'var(--text-muted)' }}>
                                                        "{transferReason}"
                                                    </span>
                                                </td>
                                                <td>
                                                    {isHandled ? (
                                                        <span className="badge success">✓ Handled</span>
                                                    ) : (
                                                        <span className="badge warning">Pending</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {!isHandled && !isEditing && (
                                                        <button
                                                            className="secondary"
                                                            style={{ fontSize: '13px', padding: '6px 12px' }}
                                                            onClick={() => onEdit(order, transferReason)}
                                                        >
                                                            Edit Outcome
                                                        </button>
                                                    )}
                                                    {isEditing && (
                                                        <span style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                                                            Editing below...
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>

                                            {/* Inline Edit Panel */}
                                            {isEditing && editing && (
                                                <tr>
                                                    <td colSpan={8} style={{ padding: 0, background: '#FFFEF5' }}>
                                                        <div style={{ padding: 16, borderLeft: '4px solid var(--warning-amber)' }}>
                                                            <div style={{ marginBottom: 16, padding: 12, background: '#FFF3CD', borderRadius: 8 }}>
                                                                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
                                                                    Transfer Reason: "{editing.transferReason}"
                                                                </div>
                                                                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                                                                    Customer: {editing.order.patient_name} | Phone: {editing.order.phone}
                                                                </div>
                                                            </div>

                                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                                                <label style={{ display: 'grid', gap: 6 }}>
                                                                    <span style={{ fontSize: 13, fontWeight: 600 }}>New Outcome</span>
                                                                    <select
                                                                        value={editing.outcome}
                                                                        onChange={e => {
                                                                            const nextOutcome = e.target.value;
                                                                            onUpdateEditing({
                                                                                outcome: nextOutcome,
                                                                                cancellationReason: nextOutcome === 'cancelled' ? (editing.cancellationReason ?? '') : undefined,
                                                                            });
                                                                        }}
                                                                        style={{ padding: '8px 12px' }}
                                                                    >
                                                                        {liveTransferOutcomeOptions.map(opt => (
                                                                            <option key={opt.value} value={opt.value}>
                                                                                {opt.label}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                </label>

                                                                {(editing.outcome === 'confirmed' || editing.outcome === 'reschedule') && (
                                                                    <label style={{ display: 'grid', gap: 6 }}>
                                                                        <span style={{ fontSize: 13, fontWeight: 600 }}>Delivery Window</span>
                                                                        <WindowSelect
                                                                            value={editing.window}
                                                                            onChange={(w) => onUpdateEditing({ window: w })}
                                                                        />
                                                                    </label>
                                                                )}

                                                                {editing.outcome === 'reschedule' && (
                                                                    <label style={{ display: 'grid', gap: 6 }}>
                                                                        <span style={{ fontSize: 13, fontWeight: 600 }}>New Delivery Date</span>
                                                                        <input
                                                                            type="date"
                                                                            value={editing.date}
                                                                            onKeyDown={() => { dateTypingRef.current = true; }}
                                                            onChange={e => {
                                                                const v = e.target.value;
                                                                if (!v) {
                                                                    if (!dateTypingRef.current) onUpdateEditing({ date: '' });
                                                                    dateTypingRef.current = false;
                                                                    return;
                                                                }
                                                                dateTypingRef.current = false;
                                                                const parts = v.split('-');
                                                                let sanitized = v;
                                                                if (parts.length === 3) {
                                                                    const month = parts[1] === '00' ? '01' : parts[1];
                                                                    const day = parts[2] === '00' ? '01' : parts[2];
                                                                    sanitized = `${parts[0]}-${month}-${day}`;
                                                                }
                                                                onUpdateEditing({ date: sanitized });
                                                            }}
                                                                            style={{ padding: '8px 12px' }}
                                                                        />
                                                                    </label>
                                                                )}

                                                                {editing.outcome === 'cancelled' && (
                                                                    <label style={{ display: 'grid', gap: 6 }}>
                                                                        <span style={{ fontSize: 13, fontWeight: 600 }}>Cancellation Reason</span>
                                                                        <textarea
                                                                            value={editing.cancellationReason ?? ''}
                                                                            onChange={e => onUpdateEditing({ cancellationReason: e.target.value })}
                                                                            style={{ padding: '8px 12px', minHeight: 60 }}
                                                                        />
                                                                    </label>
                                                                )}
                                                            </div>

                                                            <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                                                <button className="secondary" onClick={onCancelEdit}>
                                                                    Cancel
                                                                </button>
                                                                <button className="primary" onClick={onSaveEdit}>
                                                                    Save & Next
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Successfully Processed Section */}
            {processedOrders.length > 0 && (
                <div className="table-container table-container--scroll-inner" style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ padding: '16px', borderBottom: '1px solid var(--border)', background: 'var(--neutral-gray)', flexShrink: 0 }}>
                        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
                            ✓ Successfully Processed: {processedOrders.length}
                        </h3>
                    </div>
                    <div className="table-scroll-wrapper">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>SM</th>
                                    <th>Patient</th>
                                    <th>City</th>
                                    <th>Date</th>
                                    <th>Time Required</th>
                                    <th>White Glove</th>
                                    <th>Window</th>
                                    <th>Outcome</th>
                                </tr>
                            </thead>
                            <tbody>
                                {processedOrders.map(o => {
                                    if (!o) return null;
                                    return (
                                        <tr key={o.id} className={rowClass(o.white_glove_service)}>
                                            <td><strong>{o.sm_number}</strong></td>
                                            <td>{o.patient_name}</td>
                                            <td>{getOrderCity(o)}</td>
                                            <td>{formatDateWithYear(o.delivery_date)}</td>
                                            <td>{formatTimeRequired(o.time_required)}</td>
                                            <td>{whiteGloveLabel(o.white_glove_service)}</td>
                                            <td>{formatWindow(o)}</td>
                                            <td><OutcomeBadge outcome={o.last_outcome} /></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
